import { RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { MetricProgress } from "../marketing-goals/metric-progress-calc";
import { useMetricProgress } from "../marketing-goals/use-metric-progress";
import { metricFallsShortOfGoal } from "./plan-health";

export interface PlanHealth {
    isLoading: boolean;
    hasRegion: boolean;
    hasNoTouchpoints: boolean;
    hasTouchpointGoal: boolean;
    isFallingShortOfGoal: boolean;
}

const fallsShortOfGoal = (progress: MetricProgress | null): boolean =>
    progress ? metricFallsShortOfGoal(progress.target, progress.projected) : false;

const hasGoal = (progress: MetricProgress | null): boolean => (progress?.target ?? 0) > 0;

export const usePlanHealth = (): PlanHealth => {
    const regionId = useRegionId();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const trpcClient = trpc.useTRPC();

    const playbookQuery = useQuery({
        ...trpcClient.marketingPlaybook.myPlaybook.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });
    const visitProgress = useMetricProgress("visit");
    const otherTouchpointsProgress = useMetricProgress("otherTouchpoints");

    const isLoading =
        playbookQuery.isLoading ||
        visitProgress.isLoading ||
        otherTouchpointsProgress.isLoading;
    const hasNoTouchpoints = (playbookQuery.data?.touchpoints.length ?? 0) === 0;

    const hasTouchpointGoal =
        hasGoal(visitProgress.progress) || hasGoal(otherTouchpointsProgress.progress);
    const isFallingShortOfGoal =
        fallsShortOfGoal(visitProgress.progress) ||
        fallsShortOfGoal(otherTouchpointsProgress.progress);

    return {
        isLoading,
        hasRegion: !!regionId,
        hasNoTouchpoints,
        hasTouchpointGoal,
        isFallingShortOfGoal,
    };
};
